import React, { FunctionComponent } from 'react';
import { Box, Heading, Text } from '@wix/design-system';
import { useRouter } from 'next/router';
import { useTranslation } from '@wix/wix-i18n-config';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { BI, EDITORS_LABLES, PAGES } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { InnerLink } from '../InnerLink';
import { SearchBar } from '../SearchBar/Index';
import { useBI } from '../../hooks/useBI';
import { mapPageTypeToSourceName } from '../../utils/bi';
import { PageType } from '../../types';
import css from './SubHeader.module.scss';
import { RenderHTML } from '../RenderHTML';
import { COLORS } from '../../utils/theme';
import { Article } from '@wix/answers-api';

type SubHeaderProps = {
  pageType: PageType;
  labels: string[];
  itemId?: string;
  itemType?: string;
  article?: Article;
  isRoadmapArticlePage?: boolean;
};

const BACKGROUND_COLORS = {
  DEFAULT: COLORS.D10,
  EDITOR_X: '#4842ce',
  ADI: '#007d60',
  EDITOR: '#0069ff',
};

export const SubHeader: FunctionComponent<SubHeaderProps> = ({
  pageType,
  labels,
  itemId,
  itemType,
  isRoadmapArticlePage,
}: SubHeaderProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const isEditorLabel = labels.includes(EDITORS_LABLES.EDITOR);
  const isEditorXLabel = labels.includes(EDITORS_LABLES.EDITOR_X);
  const isADILabel = labels.includes(EDITORS_LABLES.ADI);
  const isEditorsLabel = isEditorLabel || isEditorXLabel || isADILabel;

  const getBackgroundColor = (): string => {
    if (isEditorsLabel) {
      if (isEditorLabel) {
        return BACKGROUND_COLORS.EDITOR;
      } else if (isEditorXLabel) {
        return BACKGROUND_COLORS.EDITOR_X;
      } else if (isADILabel) {
        return BACKGROUND_COLORS.ADI;
      }
    }
    return BACKGROUND_COLORS.DEFAULT;
  };

  const getEditorText = () => {
    if (isEditorsLabel) {
      if (isEditorLabel) {
        return t('subheader.editor');
      } else if (isEditorXLabel) {
        return t('subheader.editorx');
      } else if (isADILabel) {
        return t('subheader.adi');
      }
    }
    return '';
  };

  const onHomepageClick = async (): Promise<void> => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.HOME_PAGE,
        clicked_text: t('subheader.wix.center'),
        clicked_url: `${location.origin}/${locale}`,
      })
    );
  };

  return (
    <Box
      verticalAlign="middle"
      backgroundColor={getBackgroundColor()}
      className={`${css.wrapper} ${
        isRoadmapArticlePage || pageType === PAGES.ROADMAP
          ? css.roadmapPage
          : ''
      }`}
    >
      <Heading size="medium" light className={css.backToHomepageLink} as="div">
        <InnerLink
          href="/"
          dataHook={DATA_HOOKS.SUBHEADER_GO_TO_HOMEPAGE}
          onClick={onHomepageClick}
        >
          {t('subheader.wix.center')}
        </InnerLink>
      </Heading>
      {isEditorsLabel && (
        <Text light>
          <RenderHTML html={getEditorText()} className={css.categoryText} />
        </Text>
      )}
      <SearchBar itemId={itemId} itemType={itemType} pageType={pageType} />
    </Box>
  );
};
